// packages/web/src/components/RecordEditor.tsx — R3: the app-wide host for the
// schema-driven record engine. Mounted ONCE in App (it has the live tenantId);
// it renders nothing until a screen calls openRecordEditor(...) or
// openCreateMenu() (see lib/recordEditor.ts). Then it puts up a calm modal:
//
//   • create   → <RecordForm> for the requested type (optionally pre-filled).
//   • edit     → <RecordForm existing> + <RecordActions> (transitions / inline
//                status / RETIRE) for one record — the generic ACT surface on
//                every type, reached from a detail's Edit button.
//   • menu     → the GLOBAL create menu: every creatable type → a create form,
//                so nothing is un-creatable even without a bespoke "+ New".
//
// Any successful mutation calls bumpRecordsVersion() so the read surface behind
// the overlay refreshes. Escape / backdrop / Close all dismiss. Reuses R2's
// RecordForm + RecordActions verbatim — this file is only the shell + wiring.
import { useEffect, useRef, useState } from 'preact/hooks';
import { RecordForm } from './RecordForm';
import { RecordActions } from './RecordActions';
import { PersonAvatarField } from './Avatar';
import { AuthorityGateNotice } from './AuthorityGateNotice';
import { useAuthorityGate } from '../lib/authorityGate';
import {
  reserveSequenceNumber,
  consumeSequenceReservation,
  voidSequenceReservation,
  ApiError,
  getRecordTypes,
  getRecordTypeSchema,
  getRecordWithLinks,
  type RecordRow,
  type RecordTypeSchema,
  type RecordTypeSummary,
  type LinkRow,
  type NumberReservation,
} from '../lib/api';
import {
  bumpRecordsVersion,
  closeCreateMenu,
  closeRecordEditor,
  openRecordEditor,
  useCreateMenuOpen,
  useRecordEditorRequest,
  type EditorRequest,
} from '../lib/recordEditor';
import { parseRoute } from '../routes/route';
import './RecordEditor.css';

/** CONTROLLED numbering fields (Huan's second ruling, 2026-08-23:
 * "controlled not editable — lock when suggested; if it is not saved or
 * completed it's marked as such to prevent duplicate or gap"). Opening the
 * create form RESERVES the next number from the server's ledger and locks
 * the field; saving CONSUMES the reservation; cancelling VOIDS it; a
 * closed browser's orphan is lazily voided server-side. Pack vocabulary
 * the web already owns (FindSheet's registry precedent). */
const CREATE_SEQUENCE_FIELDS: Record<string, string> = {
  hvac_work_order: 'wo_number',
};

/** A capture-created record arrives with "NEW-<timestamp>" from the pack's
 * requiredFieldFallbacks — a placeholder that READS as unnumbered by design.
 * Mirrors the server's registry (records.ts). */
const SEQUENCE_PLACEHOLDER_PREFIX = 'NEW-';

/** EDIT locks an ASSIGNED number too — "controlled not editable" covers the
 * whole life of the value, and the server now refuses the change anyway. An
 * UNNUMBERED record (empty, or a capture placeholder) keeps a typable field:
 * renumbering it once is the designed repair path ("whoever answers the ask
 * renumbers it"), booked through the server's ledger. */
function editLockedFields(typeKey: string, fields: Record<string, unknown>): string[] | undefined {
  const field = CREATE_SEQUENCE_FIELDS[typeKey];
  if (!field) return undefined;
  const v = fields[field];
  const value = v === null || v === undefined ? '' : String(v).trim();
  const unnumbered = value === '' || value.startsWith(SEQUENCE_PLACEHOLDER_PREFIX);
  return unnumbered ? undefined : [field];
}

/** "Where you stood" — the record the CURRENT screen is about, if it is a
 * detail screen. The + menu creates from anywhere; a create launched while
 * standing on a client/site/job/tech screen inherits that record as context
 * (Huan's live walk, 2026-08-23 — the detail screens' own buttons already
 * passed prefills; the global menu passed nothing). The screen->type map is
 * pack vocabulary the web already owns (FindSheet's registry precedent). */
const DETAIL_SCREEN_TYPES: Record<string, string> = {
  clientDetail: 'hvac_client',
  siteDetail: 'hvac_site',
  workOrderDetail: 'hvac_work_order',
  techDetail: 'hvac_tech_profile',
};
function routeContext(): { typeKey: string; id: string } | null {
  const route = parseRoute(window.location.hash.replace(/^#/, '') || '/');
  const r = route as { screen?: string; id?: string };
  const typeKey = r.screen ? DETAIL_SCREEN_TYPES[r.screen] : undefined;
  return typeKey && r.id ? { typeKey, id: r.id } : null;
}

/** Mounted once at the shell level. Renders the create menu and/or the record
 * editor overlay when the store asks for them; otherwise nothing. */
export function RecordEditorHost({ tenantId }: { tenantId: string }) {
  const request = useRecordEditorRequest();
  const menuOpen = useCreateMenuOpen();
  return (
    <>
      {menuOpen && <CreateMenu tenantId={tenantId} onClose={closeCreateMenu} />}
      {request && <EditorOverlay tenantId={tenantId} request={request} onClose={closeRecordEditor} />}
    </>
  );
}

/** A dismissible modal shell — backdrop click + Escape close; the panel traps
 * neither focus loudly nor animates against reduced-motion (see the CSS). */
function Modal({ onClose, labelledBy, children }: { onClose: () => void; labelledBy: string; children: preact.ComponentChildren }) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    // Move focus into the panel so the keyboard lands somewhere sensible.
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div class="re-overlay" onClick={onClose}>
      <div
        class="re-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function EditorOverlay({
  tenantId,
  request,
  onClose,
}: {
  tenantId: string;
  request: EditorRequest;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} labelledBy="re-title">
      <div class="re-head">
        <span class="re-head__eyebrow" id="re-title">
          {request.mode === 'create' ? 'New record' : 'Edit record'}
        </span>
        <button type="button" class="re-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      {request.mode === 'create' ? (
        <CreatePane tenantId={tenantId} request={request} onClose={onClose} />
      ) : (
        <EditPane tenantId={tenantId} typeKey={request.typeKey} id={request.id} onClose={onClose} />
      )}
    </Modal>
  );
}

function CreatePane({
  tenantId,
  request,
  onClose,
}: {
  tenantId: string;
  request: Extract<EditorRequest, { mode: 'create' }>;
  onClose: () => void;
}) {
  const seqField = CREATE_SEQUENCE_FIELDS[request.typeKey];
  // ⚑ A REFUSAL BECOMES A REACH ON THE GENERIC FORM TOO (Day 3 walk, S2.9).
  // This is the door a child actually uses — + › Audition › New Record — and
  // it was the one caller of RecordForm that never looked at the 409's body,
  // so the server's offer was thrown away and the screen read "request
  // failed". Same helper as every other write path; no second copy of it.
  const authority = useAuthorityGate(tenantId);
  const [reservation, setReservation] = useState<NumberReservation | null>(null);
  const [reserveFailed, setReserveFailed] = useState(false);
  // The reservation's FATE must be decided exactly once: consumed on save,
  // voided on cancel/close. A ref survives the close callbacks.
  const decidedRef = useRef(false);
  useEffect(() => {
    if (!seqField) return;
    let alive = true;
    reserveSequenceNumber(tenantId, request.typeKey, seqField)
      .then((r) => alive && setReservation(r))
      .catch(() => alive && setReserveFailed(true));
    return () => {
      alive = false;
    };
  }, [tenantId, request.typeKey, seqField]);

  function decideVoid() {
    if (!seqField || !reservation || decidedRef.current) return;
    decidedRef.current = true;
    void voidSequenceReservation(tenantId, reservation.id).catch(() => undefined);
    // Best-effort: an undelivered void is caught by the server's lazy
    // stale-void on the next reserve — the ledger stays truthful either way.
  }

  if (seqField && !reservation && !reserveFailed) {
    return <div class="re-body re-loading">Reserving the next number…</div>;
  }
  if (seqField && reserveFailed) {
    return (
      <div class="re-body">
        <div class="re-error" role="alert">
          Could not reserve the next number — check the connection and reopen this form.
        </div>
      </div>
    );
  }
  return (
    <div class="re-body">
      <RecordForm
        tenantId={tenantId}
        typeKey={request.typeKey}
        prefillFields={
          seqField && reservation
            ? { ...(request.prefillFields ?? {}), [seqField]: reservation.value }
            : request.prefillFields
        }
        prefillRels={request.prefillRels}
        contextRecord={request.contextRecord}
        lockedFields={seqField ? [seqField] : undefined}
        onSaveError={authority.handleSaveError}
        onSaved={(saved) => {
          if (seqField && reservation && !decidedRef.current) {
            decidedRef.current = true;
            void consumeSequenceReservation(tenantId, reservation.id, saved.id).catch(() => undefined);
          }
          bumpRecordsVersion();
          onClose();
        }}
        onCancel={() => {
          decideVoid();
          onClose();
        }}
      />
      <AuthorityGateNotice gate={authority.gate} />
    </div>
  );
}

function EditPane({
  tenantId,
  typeKey,
  id,
  onClose,
}: {
  tenantId: string;
  typeKey: string;
  id: string;
  onClose: () => void;
}) {
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  const [existing, setExisting] = useState<{ record: RecordRow; links: LinkRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // An EDIT can leave a person's authority exactly as a create can — the
  // server answers both with the same offer, so this door answers it the same
  // way (Day 3 walk, S2.9).
  const authority = useAuthorityGate(tenantId);
  /** ⚑ ONE SHEET, TWO COMPONENTS (Day 2 walk, S3.14). The form below and the
   * State row beneath it are read as one thing by the person using them, so
   * what the form is holding has to be here when the State row acts. The form
   * writes into this ref as they type; the transition sends it. */
  const pendingFields = useRef<Record<string, unknown>>({});

  useEffect(() => {
    let alive = true;
    setError(null);
    pendingFields.current = {};
    Promise.all([getRecordTypeSchema(tenantId, typeKey), getRecordWithLinks(tenantId, id)])
      .then(([s, r]) => {
        if (!alive) return;
        setSchema(s);
        setExisting(r);
      })
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'Could not load the record.'));
    return () => {
      alive = false;
    };
  }, [tenantId, typeKey, id, nonce]);

  if (error) {
    return (
      <div class="re-body">
        <div class="re-error" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!schema || !existing) {
    return (
      <div class="re-body">
        <div class="re-loading">Loading…</div>
      </div>
    );
  }

  return (
    <div class="re-body">
      {/* WS5: a person's avatar upload lives at the top of their editor — the
          image slot Wikidata can't fill, shown wherever the person appears. */}
      {typeKey === 'actor_person' && (
        <PersonAvatarField
          tenantId={tenantId}
          personId={existing.record.id}
          name={
            (typeof existing.record.fields['full_name'] === 'string' &&
              (existing.record.fields['full_name'] as string)) ||
            existing.record.display_name
          }
          onUploaded={bumpRecordsVersion}
        />
      )}
      <RecordForm
        tenantId={tenantId}
        typeKey={typeKey}
        schema={schema}
        existing={existing}
        pendingFields={pendingFields}
        lockedFields={editLockedFields(typeKey, existing.record.fields)}
        onSaveError={authority.handleSaveError}
        onSaved={() => {
          bumpRecordsVersion();
          onClose();
        }}
        onCancel={onClose}
      />
      <AuthorityGateNotice gate={authority.gate} />
      <RecordActions
        tenantId={tenantId}
        schema={schema}
        record={existing.record}
        pendingFields={pendingFields}
        onChanged={() => {
          // An act (transition / status) reloads the record in place so the
          // next legal states are offered, and refreshes the surface behind us.
          bumpRecordsVersion();
          setNonce((n) => n + 1);
        }}
        // S10: the record was RETIRED (archived), not deleted. The overlay
        // still closes and the surface behind it still refreshes — the only
        // thing that changed is what happened to the record.
        onRetired={() => {
          bumpRecordsVersion();
          onClose();
        }}
      />
    </div>
  );
}

function CreateMenu({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const [types, setTypes] = useState<RecordTypeSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getRecordTypes(tenantId)
      .then((t) => alive && setTypes(t))
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'Could not load the list.'));
    return () => {
      alive = false;
    };
  }, [tenantId]);

  return (
    <Modal onClose={onClose} labelledBy="re-menu-title">
      <div class="re-head">
        <span class="re-head__eyebrow" id="re-menu-title">
          Create
        </span>
        <button type="button" class="re-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div class="re-body">
        {error && (
          <div class="re-error" role="alert">
            {error}
          </div>
        )}
        {!types && !error && <div class="re-loading">Loading…</div>}
        {types && (
          <div class="re-menu" role="menu">
            {/* ⚑ W3 — A TYPE THIS TIER MAY NOT WRITE IS NOT OFFERED HERE.
                This menu is the app's only complete list of creatable types,
                and it renders whatever the server hands back — so without
                this filter a Free household would be shown "Resume version"
                and the create would 403. "The client must not offer what the
                tier does not include… without this the product shows a family
                a button that always errors, which reads as broken rather than
                as an upsell" (plugins/plan.ts's own header).

                FILTERED SILENTLY HERE, AND ONLY HERE, on purpose: this is a
                generic list with nowhere to put an explanation, and a teaser
                in a menu of eighteen nouns is noise. The surface that OWNS
                the feature says why it is absent — the resume screen carries
                the named banner. `writeLocked` is the server's answer from
                the same resolution that refuses the write, never a second
                derivation from the plan's feature list. */}
            {types
              .filter((t) => !t.writeLocked)
              .map((t) => (
                <button
                  type="button"
                  class="re-menu__item"
                  role="menuitem"
                  key={t.key}
                  onClick={() =>
                    openRecordEditor({
                      mode: 'create',
                      typeKey: t.key,
                      ...(routeContext() ? { contextRecord: routeContext()! } : {}),
                    })
                  }
                  data-testid={`create-type-${t.key}`}
                >
                  <span class="re-menu__label">{t.label}</span>
                  <span class="re-menu__arrow" aria-hidden="true">
                    ›
                  </span>
                </button>
              ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
